import { useState, useRef, forwardRef, useMemo } from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Input, AutoComplete } from 'antd';
import { BaseOptionType } from 'antd/lib/select';
import type { BaseSelectRef } from 'rc-select';

import { SEARCH_BY_COUNTY_KEYWORD } from '../../common/consts';
import useI18n from '../../common/hooks/useI18n';
import { OfficeSearchOption } from '../../common/helpers/office.utils';

type Props = {
  height: string;
  value: string;
  options: OfficeSearchOption[];
  groupdedByCity: boolean;
  onValueChange: (value: string) => void;
  onSelect: (option: OfficeSearchOption) => void;
  buttonBackgroundColor?: string;
  buttonColor?: string;
  inputPadding?: string;
  className?: string;
  theme: DefaultTheme;
};

const StyledAutoComplete = styled(AutoComplete)<{
  height: string;
  $buttonBackgroundColor: string;
  $buttonColor: string;
  $inputPadding: string;
}>`
  .ant-input-affix-wrapper {
    border-color: #d9d9d9;
    border-right-width: 0;
    &:focus,
    &:hover {
      border-color: #d9d9d9;
      border-right-width: 0;
      box-shadow: none;
    }
  }

  .ant-input-affix-wrapper-focused {
    border-right-width: 0;
    box-shadow: none;
  }

  .ant-input-group .ant-input-affix-wrapper:not(:last-child) {
    height: ${(props) => props.height};
    border-top-left-radius: calc(${(props) => props.height} / 2);
    border-bottom-left-radius: calc(${(props) => props.height} / 2);
    padding-left: 11px;
    padding-right: 11px;
    @media ${(props) => props.theme.breakpoints.xs} {
      padding-left: 8px;
      padding-right: 8px;
    }
  }

  .ant-input-affix-wrapper > input.ant-input {
    padding-left: ${(props) => props.$inputPadding};
    font-size: 16px;

    @media ${(props) => props.theme.breakpoints.xs} {
      font-size: 14px;
    }
  }

  .ant-input-group-addon:last-child {
    left: 0 !important;
    border-top-right-radius: calc(${(props) => props.height} / 2);
    border-bottom-right-radius: calc(${(props) => props.height} / 2);
  }

  .ant-input-search-button {
    height: ${(props) => props.height};
    background-color: white;
    padding: 8px;
    border-color: #d9d9d9;
    border-left-width: 0;
  }

  .ant-input-search
    > .ant-input-group
    > .ant-input-group-addon:last-child
    .ant-input-search-button {
    padding: 0 6px 0 0;
    border-top-right-radius: calc(${(props) => props.height} / 2);
    border-bottom-right-radius: calc(${(props) => props.height} / 2);
  }

  .anticon-search {
    background-color: ${(props) => props.$buttonBackgroundColor};
    color: ${(props) => props.$buttonColor};

    padding: 10px;
    height: 36px;
    width: 36px;
    border-radius: 18px;
  }
`;

const renderTitle = (title: string) => {
  return <span>{title}</span>;
};

const renderItem = (title: string) => {
  return {
    value: title,
    label: <div>{title.trim()}</div>,
  };
};

const OfficesSearchBar = forwardRef<any, Props>(
  (
    {
      height,
      value,
      options,
      groupdedByCity,
      onValueChange,
      onSelect,
      buttonBackgroundColor,
      buttonColor = 'white',
      inputPadding = '14px',
      className,
      theme,
    },
    ref
  ) => {
    const i18n = useI18n();

    const [open, setOpen] = useState(false);

    let autoCompleteRef = useRef(null);
    if (ref) {
      autoCompleteRef = ref as React.MutableRefObject<BaseSelectRef>;
    }

    const counties = useMemo(() => {
      const countiesSet = new Set<string>();
      options.forEach((option) => {
        countiesSet.add(option.county);
      });
      return Array.from(countiesSet);
    }, [options]);
    const cities = useMemo(() => {
      const citiesSet = new Set<string>();
      options.forEach((option) => {
        citiesSet.add(option.city);
      });
      return Array.from(citiesSet);
    }, [options]);
    const autoCompleteOptions = useMemo(() => {
      const result: BaseOptionType[] = [];
      const countiesOrCities = groupdedByCity ? cities : counties;

      for (const countyOrCity of countiesOrCities) {
        result.push({
          label: renderTitle(
            `${countyOrCity}${countyOrCity ? '' : SEARCH_BY_COUNTY_KEYWORD}`
          ),
          options: options
            .filter((option) =>
              groupdedByCity
                ? option.city === countyOrCity
                : option.county === countyOrCity
            )
            .map((option) => renderItem(option.text)),
        });
      }

      return result;
    }, [cities, counties, groupdedByCity, options]);

    return (
      <div className={className}>
        <StyledAutoComplete
          popupClassName="search-dropdown"
          $buttonBackgroundColor={buttonBackgroundColor || theme.colors.primary}
          $buttonColor={buttonColor}
          value={value}
          open={open}
          options={autoCompleteOptions}
          height={height}
          $inputPadding={inputPadding}
          style={{ width: '100%' }}
          ref={autoCompleteRef}
          onSearch={(newValue) => {
            setOpen(!!newValue);
            onValueChange(newValue);
          }}
          onSelect={(autoCompleteOption) => {
            const selectedOption = options.find(
              (o) => o.text === autoCompleteOption
            );
            if (selectedOption) {
              onSelect(selectedOption);
            }
            setOpen(false);
            setTimeout(() => {
              if (autoCompleteRef.current) {
                autoCompleteRef.current.blur();
              }
            });
          }}
          onFocus={() => {
            setOpen(!!value);
          }}
          onBlur={() => {
            setOpen(false);
          }}
        >
          <Input.Search
            size="large"
            placeholder={i18n.t('offices.searchBar.placeholder')}
            allowClear={true}
            enterButton
          />
        </StyledAutoComplete>
      </div>
    );
  }
);

OfficesSearchBar.displayName = 'OfficesSearchBar';

export default withTheme(OfficesSearchBar);
